import type MarkdownIt from 'markdown-it';

export type UserMentionAttrs = {
  userId: string;
  label: string;
};

const MENTION_HREF_RE = /^mention:([A-Za-z0-9_-]+)$/;

/**
 * Parses `[@Label](mention:userId)` link shape into mention attrs.
 */
export function parseUserMentionLink(
  label: string,
  href: string,
): UserMentionAttrs | null {
  const match = href.trim().match(MENTION_HREF_RE);
  const trimmedLabel = label.trim();
  if (!match?.[1] || !trimmedLabel) {
    return null;
  }
  return { userId: match[1], label: trimmedLabel };
}

export function serializeUserMentionAttrs(attrs: UserMentionAttrs): string {
  const label = attrs.label.replace(/[[\]]/g, '');
  return `[@${label}](mention:${attrs.userId})`;
}

/**
 * Turns markdown-it link tokens whose href is `mention:…` into `user_mention`
 * tokens so the editor can map them to an inline atom instead of a hyperlink.
 */
export function userMentionTokenizer(md: MarkdownIt): void {
  md.core.ruler.after('inline', 'user_mention', (state) => {
    for (const block of state.tokens) {
      if (block.type !== 'inline' || !block.children) {
        continue;
      }
      const children = block.children;
      const next: typeof children = [];
      for (let i = 0; i < children.length; i++) {
        const open = children[i];
        const text = children[i + 1];
        const close = children[i + 2];
        if (
          open?.type === 'link_open' &&
          text?.type === 'text' &&
          close?.type === 'link_close'
        ) {
          const href = open.attrGet('href') ?? '';
          const attrs = parseUserMentionLink(text.content, href);
          if (attrs) {
            const token = new state.Token('user_mention', '', 0);
            token.meta = attrs;
            token.content = attrs.label;
            next.push(token);
            i += 2;
            continue;
          }
        }
        if (open) {
          next.push(open);
        }
      }
      block.children = next;
    }
  });
}
